import { ReactNode } from 'react'

export interface LegalSection {
  id: string
  title: string
  content: ReactNode
}

interface LegalDocumentLayoutProps {
  title: string
  subtitle: string
  effectiveDate: string
  lastUpdated: string
  sections: LegalSection[]
  introCallout?: ReactNode
}

const LegalDocumentLayout = ({
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  sections,
  introCallout,
}: LegalDocumentLayoutProps) => {
  return (
    <div className="section-shell">
      <div className="container-shell">
        <div className="surface-card p-7 sm:p-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="eyebrow">Legal Document</span>
            <span className="chip">Effective: {effectiveDate}</span>
            <span className="chip">Updated: {lastUpdated}</span>
          </div>

          <h1 className="h-display mt-6 text-balance text-4xl sm:text-5xl">{title}</h1>
          <p className="text-body mt-4 max-w-3xl">{subtitle}</p>
        </div>

        {introCallout ? <div className="surface-card mt-6 p-6 text-sm text-ink-700">{introCallout}</div> : null}

        <div className="mt-8 grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="surface-card p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-500">On this page</p>
              <nav className="mt-4">
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="text-sm font-medium text-ink-600 transition hover:text-ink-500"
                      >
                        {section.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            <div className="surface-card p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-ink-500">Need help?</p>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">
                Contact support at{' '}
                <a href="mailto:support@asterode.ai" className="font-semibold text-ink-500 hover:text-ink-700">
                  support@asterode.ai
                </a>
                .
              </p>
            </div>
          </aside>

          <article className="space-y-5">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="surface-card scroll-mt-24 p-6 sm:p-8">
                <h2 className="h-display text-2xl sm:text-3xl">{section.title}</h2>
                <div className="mt-4 space-y-4 text-sm leading-relaxed text-ink-700 sm:text-base">
                  {section.content}
                </div>
              </section>
            ))}
          </article>
        </div>
      </div>
    </div>
  )
}

export default LegalDocumentLayout

